import { CollectionViewer } from '@angular/cdk/collections';

import { moveItemInArray } from '@angular/cdk/drag-drop';
import { DataSource } from '@angular/cdk/table';

import { DecimalPipe, PercentPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  OnDestroy,
  signal,
} from '@angular/core';
import { translate, TranslocoService } from '@jsverse/transloco';

import { saveAs } from 'file-saver-es';
import { capitalize, capitalizeAll, formatNumber } from 'humanize-plus';
import { BehaviorSubject, Observable } from 'rxjs';

import { Constants } from 'app/constants/constants';
import { DownloadAsDialogResult } from 'app/modules/collection/components/download-as-dialog/download-as-dialog.component';

import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Comment, CommentPoint, CommentTarget } from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import { LexDatePipe } from '../../../../pipes/lex-date.pipe';
import { MatchmakerHandler } from '../../../../providers/semantria-api/spote.service';
import { DocumentService } from '../../../../providers/storage-api/document.service';
import { ProjectService } from '../../../../providers/storage-api/project.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';
import { LoggerService } from '../../../../services/logger.service';
import { SystemService } from '../../../../services/system.service';
import { WidgetLimiterService } from '../../../../services/widget-limiter.service';
import { FilterService, WidgetQueryOptions } from '../../../collection/services/filter.service';
import { DashboardViewComponent } from '../../../dashboard/pages/dashboard-view/dashboard-view.component';
import { SelectedSection } from '../section-selector/section-selector.component';
import { AbstractTableWidgetComponent } from '../widget/abstract-table-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import {
  ChartSort,
  DEFAULT_SORT,
  WidgetChartOptions,
} from '../widget-chart/widget-chart.component';
import {
  ChartAiInsightPointSelectionService,
  ChartPointSelectionMatchmakerOptions,
} from '../widget-expansion-panel/widget-ai-insights/chart-ai-insight-point-selection.service';
import { ChartCommentsService } from '../../services/chart-comments.service';
import { ChartDisplayService } from '../../services/chart-display.service';
import { CrosstabChartReport } from '../../services/chart-report';
import {
  COMMENT_POINT_ID_SEPARATOR,
  CrosstabData,
  CrosstabDataItem,
} from '../../services/chart-report/chart-report-crosstab/chart-report-crosstab';
import { ChartReportService } from '../../services/chart-report.service';
import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

const HEAT_SCALE = 5;
const HEAT_COLORS: Record<number, { fill: string; color: string }> = {
  0: { fill: 'ffffff', color: '000000' },
  1: { fill: 'd0e3f3', color: '000000' },
  2: { fill: 'a2c7e7', color: '000000' },
  3: { fill: '73abdb', color: 'ffffff' },
  4: { fill: '458fcf', color: 'ffffff' },
  5: { fill: '1274d1', color: 'ffffff' },
};
const HEADER_CELL_STYLE = { fill: '949494', color: 'ffffff', bold: true };
const SELF_OCCURRENCE_FILL = 'c9c9c9';

export interface CrosstabWidgetOptions extends WidgetChartOptions {
  display: {
    heatmap: boolean;
    showAsPercentage: boolean;
    decimalPrecision: number;
  };
  rowSelectedSections?: SelectedSection[];
  columnSelectedSections?: SelectedSection[];
  columnAnalyticItem: string;
  columnEntityTypeFilter?: string;
  columnCustomFieldSelected?: boolean;
  columnStandardFieldSelected?: boolean;
  columnSectionPropertySelected?: boolean;
  columnSelectedItems?: string[];
  columnSort?: ChartSort;
}

class CrosstabDataSource extends DataSource<any> {
  private ds: BehaviorSubject<any[]>;

  _displayedColumns = [];
  originalDisplayedColumns = [];
  displayedColumnIds = [];
  data: CrosstabDataItem[];
  originalData: CrosstabDataItem[];
  totalHits: number;
  documentCounts: { [key: string]: { [key: string]: number } };

  taxonomyData = {};

  connect(_collectionViewer: CollectionViewer): Observable<any[] | ReadonlyArray<any>> {
    this.ds = new BehaviorSubject<any[]>(this.data);
    return this.ds.asObservable();
  }

  disconnect(_collectionViewer: CollectionViewer): void {
    this.ds.complete();
  }

  setData(ctData: CrosstabData) {
    this.displayedColumns = ctData.columns;
    this.originalDisplayedColumns = [...this.displayedColumns];
    this.data = ctData.data;
    this.originalData = [...this.data];
    this.taxonomyData = ctData.taxonomyData;
    this.totalHits = ctData.totalHits;
    this.documentCounts = ctData.documentCounts;
    if (this.ds) {
      this.ds.next(this.data);
    }
  }

  update(data, displayedColumns) {
    this.data = [...data];
    this.displayedColumns = [...displayedColumns];
    if (this.ds) {
      this.ds.next(this.data);
    }
  }

  clearData() {
    this.displayedColumns = [];
    this.data = [];
    this.taxonomyData = {};
  }

  generateColumnId(column) {
    return `column-${column}`;
  }

  get displayedColumns() {
    return this._displayedColumns;
  }

  set displayedColumns(columns) {
    this._displayedColumns = columns;
    this.displayedColumnIds = columns.map((c) => this.generateColumnId(c));
  }
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-crosstab',
  templateUrl: './widget-crosstab.component.html',
  styleUrls: ['./widget-crosstab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DecimalPipe, PercentPipe, ChartAiInsightPointSelectionService],
  standalone: false,
})
export class WidgetCrosstabComponent
  extends AbstractTableWidgetComponent<CrosstabWidgetOptions>
  implements OnInit, OnChanges, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() isCustomSortMode;

  @Output() isCustomSortModeChange = new EventEmitter<boolean>();

  @ViewChild('tableSplitter') tableSplitter: ElementRef;
  @ViewChild('fakeTableSplitter') fakeTableSplitter: ElementRef;
  @ViewChild('columnDropList') columnDropList: ElementRef;

  public downloadFormats: string[] = ['pptx', 'image', 'csv'];
  public canFilter = true;
  public hasAnalyses = true;

  dataSource: CrosstabDataSource;

  private report: CrosstabChartReport;

  private maxValue: number;
  private minValue: number;
  private range: number;
  private chartInitialized: boolean;
  private fieldTypeMap: Map<string, string>;

  isRowDate: boolean;
  isColumnDate: boolean;
  isRowDouble: boolean;
  isColumnDouble: boolean;
  isSingleItem: boolean;

  isRowDraggable: boolean;

  rowsBeforeCustomOrder: any[];
  columnsBeforeCustomOrder: any[];

  downloadingSpoteRequest?: boolean;
  disablePinnedCommentClick = false;
  pinnedComment: Comment;
  rowKey = CrosstabChartReport.KEY;
  readonly TABLE_DEFAULT_DRAG_DELAY = 200;
  readonly tableWidth = 12; // in inches

  selectedInsightPoints = signal([]);

  @HostListener('window:scroll', [])
  onScroll() {
    if (this.menuTriggerElement && this.menuTriggerElement.menuOpen) {
      this.menuTriggerElement.closeMenu();
    }
  }

  constructor(
    private logger: LoggerService,
    private chartDisplayService: ChartDisplayService,
    private chartCommentsService: ChartCommentsService,
    private lexDatePipe: LexDatePipe,
    private chartReportService: ChartReportService,
    private translocoService: TranslocoService,
    private decimalPipe: DecimalPipe,
    private percentPipe: PercentPipe,
    private chartPointSelectionService: ChartAiInsightPointSelectionService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected filterService: FilterService,
    protected dialogService: DialogService,
    protected documentService: DocumentService,
    protected alertService: AlertService,
    protected widgetLimiterService: WidgetLimiterService,
    protected dashboardViewComponent: DashboardViewComponent,
    protected widgetComponent: WidgetComponent,
    protected projectService: ProjectService,
    protected systemService: SystemService,
    protected timezoneService: TimezoneService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      filterService,
      dialogService,
      documentService,
      projectService,
      alertService,
      widgetLimiterService,
      interactiveSmartSummaryService,
      dashboardViewComponent,
      widgetComponent,
      systemService,
      accountService,
      authService,
      timezoneService,
      customFieldService,
      commentsHandlerService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    super.updateFilterList();
    this.dataSource = new CrosstabDataSource();
    if (!this.widget.payload.graphType) {
      this.widget.payload.graphType = 'crosstab';
    }

    this.chartReportService.fieldTypeMapPromise.then((map) => {
      this.fieldTypeMap = map;
      this.checkIfCustomDate();
      this.updateTitle.emit();

      this.refresh(false, true);
    });

    this.translocoService.selectTranslateObject('analytic-items').subscribe(() => {
      this.updateTitle.emit();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isCustomSortMode?.currentValue) {
      this.rowsBeforeCustomOrder = [...this.dataSource.data];
      this.columnsBeforeCustomOrder = [...this.dataSource.displayedColumns];
      setTimeout(() => {
        if (this.fakeTableSplitter) {
          this.fakeTableSplitter.nativeElement.style.minWidth = `${this.tableSplitter.nativeElement.offsetWidth}px`;
          this.fakeTableSplitter.nativeElement.style.maxWidth = `${this.tableSplitter.nativeElement.offsetWidth}px`;
        }

        if (this.columnDropList) {
          this.columnDropList.nativeElement.style.marginTop = `-${this.tableSplitter.nativeElement.parentElement.offsetHeight}px`;
          this.columnDropList.nativeElement.style.bottom = `-${this.tableSplitter.nativeElement.parentElement.offsetHeight}px`;
          this.columnDropList.nativeElement.style.display = 'table';
        }
      }, 100);
    }
    if (changes.widget?.currentValue) {
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.widgetLoadController.abort('destroyed');
  }

  private checkIfCustomDate() {
    if (this.fieldTypeMap) {
      this.isRowDate = this.fieldTypeMap.get(this.payload.analyticItem) === 'date';
      this.isColumnDate = this.fieldTypeMap.get(this.payload.columnAnalyticItem) === 'date';
      this.isRowDouble = this.fieldTypeMap.get(this.payload.analyticItem) === 'double';
      this.isColumnDouble = this.fieldTypeMap.get(this.payload.columnAnalyticItem) === 'double';
    }
  }

  private async loadData(initialLoad = false) {
    try {
      this.report = this.chartReportService.configureReport(
        this.widget.payload
      ) as CrosstabChartReport;
      const crosstabData = await this.report.executeReportForCrosstab(
        this.query,
        this.payload,
        this.widgetDataSource
      );
      this.filteredDocumentCount = this.report.lastDocumentCount;

      if (this.widgetLoadController.signal.aborted) {
        return;
      }

      this.dataSource.setData(crosstabData);

      this.widgetComponent.updateWidgetActionComments();

      this.dataSourceNotFound = false;
      this.noData = !crosstabData.columns.length;

      if (!this.noData) {
        this.maxValue = crosstabData.maxValue;
        this.minValue = crosstabData.minValue;
        this.range = (this.maxValue - this.minValue) / HEAT_SCALE;

        this.sortDataTable();

        if (this.isDependencyWheel) {
          this.initializeChart(crosstabData);
        }
      }
    } catch (error) {
      this.logger.error('Error occured during fetching crosstab data', error);

      if ((error.status = 404)) {
        this.dataSourceNotFound = true;
        this.isLoading = false;
        this.noData = false;
        this.dataSource.clearData();
      }
      return;
    } finally {
      this.loadingFinished.emit(initialLoad);
    }
  }

  private sortDataTable() {
    const rowSort = this.widget.payload.sort;
    const columnSort = this.widget.payload.columnSort;

    if (rowSort?.field === 'custom') {
      this.dataSource.data.sort(
        (a, b) =>
          rowSort.items.findIndex((i) => i === a[CrosstabChartReport.KEY]) -
          rowSort.items.findIndex((i) => i === b[CrosstabChartReport.KEY])
      );
    }

    if (columnSort?.field === 'custom') {
      this.dataSource.displayedColumns = this.dataSource.displayedColumns.sort(
        (a, b) => columnSort.items.indexOf(a) - columnSort.items.indexOf(b)
      );
    }

    if (rowSort && rowSort.field !== 'custom') {
      this.sortByLabelFn(rowSort, 'sort');
    }
    if (columnSort && columnSort.field !== 'custom') {
      this.sortByLabelFn(columnSort, 'columnSort');
    }
  }

  private initializeChart(report) {
    const highchartsOptions = this.report?.configureChart(this.dashboard, this.widget, report);
    const self = this;
    Object.setNestedPropertyValue(
      highchartsOptions,
      'plotOptions.series.point.events.click',
      function (e) {
        if (!self.isCustomSortMode) {
          self.openChartMenu(e);
        }
      }
    );

    highchartsOptions.annotations?.forEach((a) => {
      a.events = {
        click: this.annotationClickHandlerFn(),
      };
    });

    if (this.report?.skippedValues) {
      this.widgetInfo = {
        icon: 'warning',
        level: 'WARNING',
        tooltip: this.translocoService.translate('widgets.missing-items-no-co-occurrence', {
          count: this.report.skippedValues,
        }),
      };
    }

    setTimeout(() => {
      this.chartDisplayService.drawChart(
        this.elementContent.nativeElement,
        this.dashboard,
        this.widget,
        report,
        highchartsOptions
      );

      this.chartInitialized = true;
    });
  }

  async refresh(reset = false, initialLoad = false, refreshedDatasource?: WidgetDataSource) {
    super.refresh(reset, initialLoad, refreshedDatasource);

    if (this.dataSourceNotFound) {
      this.loadingStarted.emit(initialLoad);
      this.loadingFinished.emit(initialLoad);
      this.isLoading = false;
      return;
    }

    this.loadingStarted.emit(initialLoad);
    this.checkIfCustomDate();
    this.isSingleItem = this.checkIfSingleItem();

    this.widgetLimiterService
      .execute(async () => {
        if (this.widgetLoadController.signal.aborted) {
          return;
        }

        await this.loadData(initialLoad);
      })
      .finally(() => {
        this.isLoading = false;

        this.cdr.markForCheck();

        setTimeout(() => this.fillSeparatorCell());
      });
  }

  public redraw() {
    if (!this.chartInitialized && this.isDependencyWheel) {
      this.initializeChart({
        data: this.dataSource.data,
        columns: this.dataSource.displayedColumns,
      });
      this.loadingFinished.emit(false);
    } else if (this.isDependencyWheel) {
      this.chartDisplayService.reflowChart(this.elementContent.nativeElement);
    } else {
      this.chartInitialized = false;
    }
    this.fillSeparatorCell();
  }

  private fillSeparatorCell() {
    if (this.tableSplitter) {
      const splitter = this.tableSplitter.nativeElement;
      splitter.querySelectorAll('.crosstab__table-header-splitter').forEach((e) => {
        const rotation = Math.atan(
          splitter.parentElement.offsetHeight / splitter.parentElement.offsetWidth
        );
        e.style.transform = `rotate(${rotation}rad)`;
      });
      splitter.style.visibility = 'visible';

      if (this.fakeTableSplitter) {
        setTimeout(() => {
          this.fakeTableSplitter.nativeElement.style.minWidth = `${this.tableSplitter.nativeElement.offsetWidth}px`;
          this.fakeTableSplitter.nativeElement.style.maxWidth = `${this.tableSplitter.nativeElement.offsetWidth}px`;
        });
      }
    }
  }

  public getCellClass(value) {
    if (value === 0) {
      // zero heat is reserved to zero values, as it's 100% transparent (white)
      return `value-heat-0`;
    } else if (value === this.maxValue) {
      // max value could overflow, so give it max heat
      return `value-heat-${HEAT_SCALE}`;
    }

    const degree = Math.floor((value - this.minValue) / this.range) + 1;

    return `value-heat-${degree}`;
  }

  private getCellPptText(column: string, row: Record<string, any>): string {
    const value = row[column];
    if (column === CrosstabChartReport.KEY) {
      return this.formatHeader(value, 'row') ?? '';
    }

    if (value == null) {
      return '';
    }

    if (this.displayAsPercentage) {
      return this.percentPipe.transform(value, this.percentageDecimals) ?? '';
    }

    return this.formatCellNumber(value) ?? '';
  }

  private getCellPptOptions(column: string, row: Record<string, any>) {
    if (column === CrosstabChartReport.KEY) {
      return HEADER_CELL_STYLE;
    }

    if (!this.payload.display?.heatmap) {
      return {};
    }

    if (this.isSingleItem && row[CrosstabChartReport.KEY] === column) {
      return { fill: SELF_OCCURRENCE_FILL, color: '000000' };
    }

    const value = row[column];

    if (value == null) {
      return {};
    }

    if (value === 0) {
      return HEAT_COLORS[0];
    }

    if (value === this.maxValue || this.range === 0) {
      return HEAT_COLORS[HEAT_SCALE];
    }

    const degree = Math.floor((value - this.minValue) / this.range) + 1;
    return HEAT_COLORS[Math.max(1, Math.min(HEAT_SCALE, degree))] ?? {};
  }

  public get widgetQueryOptions(): WidgetQueryOptions {
    const widgetQueryOptions = super.widgetQueryOptions;

    widgetQueryOptions.parentAnalyticItem = this.payload.columnAnalyticItem;
    widgetQueryOptions.parentSubType = this.payload.columnEntityTypeFilter;
    widgetQueryOptions.parentIsMetadata =
      this.payload.columnCustomFieldSelected || this.payload.columnStandardFieldSelected;
    widgetQueryOptions.parentSectionPropertySelected = this.payload.columnSectionPropertySelected;
    widgetQueryOptions.analyticItems = [
      'documents',
      'topics',
      'entities',
      'phrases',
      'themes',
      'taxonomy',
    ];

    return widgetQueryOptions;
  }

  public get displayAsPercentage() {
    return this.payload.display.showAsPercentage;
  }

  public get percentageDecimals() {
    const p = this.payload.display.decimalPrecision;
    return p ? `1.${p}-${p}` : '1.0-0';
  }

  public getTooltipDisplay(item: number) {
    if (this.displayAsPercentage) {
      return `${this.decimalPipe.transform(
        item * this.dataSource.totalHits
      )} (${this.percentPipe.transform(item, '1.2-2')})`;
    } else {
      return `${this.decimalPipe.transform(item)} (${this.percentPipe.transform(
        item / this.dataSource.totalHits,
        '1.2-2'
      )})`;
    }
  }

  openChartMenu(event: any): void {
    if (this.dashboard.locked || this.widgetComponent.isExpansionPanelShowing) {
      return;
    }

    setTimeout(() => {
      this.menuTriggerNativeElement.nativeElement.setAttribute(
        'style',
        `left: ${event.chartX}px; top: ${event.chartY}px`
      );
      const rowItem = event.point.fromNode?.name ?? event.point.name;
      const columnItem = event.point.toNode?.name ?? event.point.name;

      this.menuTriggerElement.menuData = { item: rowItem, column: columnItem };

      if (rowItem === columnItem) {
        this.fillMenuDataAxis(this.menuTriggerElement.menuData, rowItem);
      }

      if (this.gethasCustomSet()) {
        this.menuTriggerElement.menuData.customSet = true;
      }

      this.itemClicked = this.menuTriggerElement.menuData;
      this.itemClicked.value = event.point.weight;

      if (this.itemClicked.value === undefined) {
        const selfNode = event.point.linksFrom[0] || event.point.linksTo[0];
        this.itemClicked.value = selfNode && selfNode.custom && selfNode.custom.selfWeight;
      }

      this.pointComments = this.report.getCommentsForPoint(event.point, this.payload.comments);
      this.pointSummaries = this.getSmartSummariesForPoint(rowItem);
      this.pointHasDocuments = this.hasPointDocuments(this.itemClicked);
      this.canCreateSmartSummary = this.pointHasRequiredDocCountForSmartSummaries(this.itemClicked);

      if (this.isSmartSummaryEnabled) {
        this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
        this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
      }
      this.downloadSmartSummaryRequestToolTip = !this.canCreateSmartSummary
        ? translate('widgets.smart-summary.unavailable-for-points-with-less-than-documents', {
            count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
          })
        : '';

      this.menuTriggerElement.openMenu();
      this.cdr.markForCheck();
    });
  }

  openCellMenu($event, row, column, extraData) {
    if (this.disablePinnedCommentClick) {
      return;
    }
    if (this.isCustomSortMode) {
      return;
    }

    super.openCellMenu($event, row, column, extraData);
  }

  hasPointDocuments(point): boolean {
    return !!(point?.axisItems?.length || point?.value > 0);
  }

  getCellMenuData(row, column, extraData) {
    const menuData: any = {};

    if (extraData === 'column') {
      menuData.item = column;
      menuData.column = column;

      this.fillMenuDataAxis(menuData, menuData.column);
    } else if (extraData === 'row') {
      menuData.item = row[CrosstabChartReport.KEY];
      menuData.row = row;

      this.fillMenuDataAxis(menuData, menuData.item);
    } else {
      menuData.row = row;
      menuData.item = row[CrosstabChartReport.KEY];
      menuData.column = column;
      menuData.documentCount = this.dataSource.documentCounts[row[CrosstabChartReport.KEY]][column];
    }

    return menuData;
  }

  private fillMenuDataAxis(menuData, item) {
    if (this.dataSource.displayedColumns.includes(item) && menuData.column !== undefined) {
      menuData.axis = 'column';
      menuData.axisItems = this.dataSource.data
        .filter((r) => r[item])
        .map((r) => r[CrosstabChartReport.KEY]);
      if (menuData.axisItems.length) {
        menuData.documentCount = Object.values(this.dataSource.documentCounts)
          .reduce((acc, curr) => {
            acc.push(curr[item]);
            return acc;
          }, [])
          .reduce((acc: number, curr: number) => acc + +curr, 0);
      }
    } else {
      const row = this.dataSource.data.find((r) => r[CrosstabChartReport.KEY] === item);
      menuData.axis = 'row';
      menuData.axisItems = this.dataSource.displayedColumns.filter(
        (c) => c !== CrosstabChartReport.KEY && row[c]
      );
      if (menuData.axisItems.length) {
        menuData.documentCount = Object.values(this.dataSource.documentCounts[item]).reduce(
          (acc: number, curr: number) => acc + +curr,
          0
        );
      }
    }
  }

  protected buildSmartSummaryMenu() {
    this.cdr.markForCheck();

    return super.buildSmartSummaryMenu();
  }

  protected getDocumentsQuery(_initialQuery?: string): string {
    const widgetQueryOptions = this.getCrossTabWidgetQueryOptions(
      this.itemClicked.item,
      this.itemClicked.column,
      this.itemClicked.axis,
      this.itemClicked.axisItems
    );

    return this.filterService.getQueryForDocuments(widgetQueryOptions);
  }

  public async showDocuments(item, column, axis: 'column' | 'row', axisItems) {
    const widgetQueryOptions = this.getCrossTabWidgetQueryOptions(item, column, axis, axisItems);

    const sectionPropertySelected =
      axis === 'row'
        ? this.payload.sectionPropertySelected
        : this.payload.columnSectionPropertySelected;

    const analyticItem =
      axis === 'row' ? this.payload.analyticItem : this.payload.columnAnalyticItem;

    const entity = axis === 'row' ? item : column;

    this.getDocuments(widgetQueryOptions, {
      sectionPropertySelected,
      analyticItem,
      entity,
    }).then(() => this.cdr.markForCheck());
  }

  public showHideItemToolTip(axis) {
    if (this.enableHideItemsOnAxis(axis)) {
      return undefined;
    } else if (
      (axis === 'row' && this.widget.payload.selectedItems.length === 1) ||
      (axis === 'column' && this.widget.payload.columnSelectedItems.length === 1)
    ) {
      return this.translocoService.translate('widgets.can-not-hide-last-item');
    }
  }

  public enableHideItemsOnAxis(axis) {
    if (axis === 'row') {
      return super.enableHideItems();
    }
    return this.widget.payload.columnSelectedItems.length !== 1;
  }

  public gethasCustomSet() {
    return (
      (this.widget.payload.selectedItems && this.widget.payload.selectedItems.length > 0) ||
      (this.widget.payload.columnSelectedItems &&
        this.widget.payload.columnSelectedItems.length > 0)
    );
  }

  public async hideCrosstabItem(item, axis: 'column' | 'row') {
    const analyticItemType =
      axis === 'row' ? this.payload.analyticItem : this.payload.columnAnalyticItem;
    const displayName = this.dataSource.taxonomyData[item]?.name || item || Constants.BLANK;
    const selectedItems =
      axis === 'row' ? this.payload.selectedItems : this.payload.columnSelectedItems;

    return super.hideItem(
      {
        name: item,
        displayName,
        type: analyticItemType,
        axis: this.isSingleItem ? 'all' : axis,
      },
      selectedItems
    );
  }

  async exportAsImage(): Promise<any> {
    if (this.isDependencyWheel) {
      return this.chartDisplayService.exportChartAsImage(
        this.elementContent.nativeElement,
        this.defaultTitle
      );
    } else {
      return super.exportAsImage();
    }
  }

  private generateTableForPptExport() {
    const defaultCellOptions = {
      align: 'center',
      valign: 'middle',
    };
    const ppTable = {
      rows: [
        this.dataSource.displayedColumns.map((col) => ({
          text: col === '__key__' ? '' : this.formatHeader(col, 'column'),
          options: {
            bold: true,
            fill: '949494',
            color: 'ffffff',
            ...defaultCellOptions,
          },
        })),
      ],
    };

    this.dataSource.data.forEach((row) => {
      const rowGroup = [];
      this.dataSource.displayedColumns.forEach((column) => {
        const cellText = this.getCellPptText(column, row);
        const cellObj = {
          text: cellText,
          options: { ...this.getCellPptOptions(column, row), ...defaultCellOptions },
        };
        rowGroup.push(cellObj);
      });
      ppTable.rows.push(rowGroup);
    });
    return ppTable;
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    this.addTitleSlideToPptx(pptx);

    const dataOptions = this.addSlideOptions({});
    const tableWidth = 12; // in inches
    let colW: any[] = this.dataSource.data.map(() => 1);
    const widthSum = colW.reduce((prev, curr) => prev + curr, 0);
    colW = colW.map((w) => ((w / widthSum) * tableWidth).toFixed(2));

    let blob;
    if (this.isDependencyWheel) {
      blob = await this.chartDisplayService.exportChartAsImage(this.elementContent.nativeElement);
    } else {
      pptx.addTableSlide(this.generateTableForPptExport(), {
        newSlideStartY: 0.5,
        colW,
        w: tableWidth,
        h: 5.5,
        data: dataOptions,
        autoPage: true,
        autoPageRepeatHeader: true,
        autoPageLineWeight: 1.0,
        border: {
          color: 'black',
          pt: 2,
          type: 'solid',
        },
      });
      return;
    }

    return this.exportImageToPptx(blob, pptx, this.widgetTitle);
  }

  async executeReportForCsv(result: DownloadAsDialogResult) {
    return this.report.executeReportForCrosstab(
      this.query,
      this.payload,
      this.widgetDataSource,
      'json',
      result.recordCount,
      result.recordCount
    );
  }

  taxonomyName(reportData, name) {
    if (reportData.taxonomyData && reportData.taxonomyData[name]) {
      return reportData.taxonomyData[name].name;
    }
    return name;
  }

  prepareDataForCsv(reportData) {
    let report = reportData.columns
      .slice(1)
      .reduce(
        (prev, curr) =>
          prev + ',' + `"${Utils.escapeCsvString(this.taxonomyName(reportData, curr))}"`,
        '" "'
      );
    report += '\n';

    for (const row of reportData.data) {
      report += reportData.columns
        .slice(1)
        .reduce(
          (prev, curr) => prev + ',' + row[curr],
          `"${Utils.escapeCsvString(this.taxonomyName(reportData, row[CrosstabChartReport.KEY]))}"`
        );
      report += '\n';
    }

    return report;
  }

  public canShowDataTable(): boolean {
    return false;
  }

  public get defaultTitle(): string {
    return `${this.getTitlePart('row')} ✕ ${this.getTitlePart('column')}`;
  }

  public get customSetLabel() {
    const rowLabel = this.payload.selectedItems
      ? this.payload.selectedItems.join(', ')
      : this.getTitlePart('row');

    const colLabel = this.payload.columnSelectedItems
      ? this.payload.columnSelectedItems.join(', ')
      : this.getTitlePart('column');

    return `${rowLabel} ✕ ${colLabel}`;
  }

  public get key() {
    return CrosstabChartReport.KEY;
  }

  private checkIfSingleItem() {
    if (this.gethasCustomSet()) {
      return false;
    }

    return (
      !this.payload.columnAnalyticItem ||
      (this.payload.analyticItem === this.payload.columnAnalyticItem &&
        (this.payload.entityTypeFilter === this.payload.columnEntityTypeFilter ||
          (!this.payload.entityTypeFilter && !this.payload.columnEntityTypeFilter)))
    );
  }

  private getTitlePart(axis: 'row' | 'column') {
    const getTitlePartForAnalyticItem = (analyticItem, entityTypeFilter, isCustomField) => {
      if (entityTypeFilter) {
        const keyMap: Record<string, string> = {
          entities: 'widgets.entity',
          taxonomy: 'widgets.taxonomy',
        };
        const item = this.translocoService.translate(keyMap[analyticItem] ?? 'widgets.topic');

        return `${item}: ${capitalizeAll(entityTypeFilter.split(',').join(', '))}`;
      }

      if (isCustomField) {
        return capitalize(FieldService.getFieldLongName(analyticItem));
      }

      if (analyticItem.includes('sectionProperties')) {
        return this.translocoService.translate(`widgets.${analyticItem}`);
      }
      return this.translocoService.translate(`analytic-items.${analyticItem}`);
    };

    if (axis === 'row' || this.isSingleItem) {
      return getTitlePartForAnalyticItem(
        this.payload.analyticItem,
        this.payload.entityTypeFilter,
        this.payload.customFieldSelected
      );
    }
    return getTitlePartForAnalyticItem(
      this.payload.columnAnalyticItem,
      this.payload.columnEntityTypeFilter,
      this.payload.columnCustomFieldSelected
    );
  }

  get isDependencyWheel() {
    return this.widget.payload.graphType === 'dependencywheel';
  }

  public formatHeader(value, axis: 'row' | 'column' = 'row', truncate?) {
    if (this.isCustomSortMode && !truncate) {
      return undefined;
    }

    if (value === '') {
      return Constants.BLANK;
    }

    if ((axis === 'row' && this.isRowDate) || (axis === 'column' && this.isColumnDate)) {
      return this.lexDatePipe.transform(value);
    }
    if ((axis === 'row' && this.isRowDouble) || (axis === 'column' && this.isColumnDouble)) {
      return formatNumber(value, 2);
    }
    if (this.dataSource.taxonomyData[value]) {
      if (truncate) {
        return Utils.truncate(this.dataSource.taxonomyData[value].name);
      }
      return this.dataSource.taxonomyData[value].name;
    }

    if (truncate) {
      return Utils.truncate(value);
    }
    return value;
  }

  getSortIcon(sortProp: 'sort' | 'columnSort', item) {
    const currentSort = this.widget.payload[sortProp];

    if (currentSort?.field === item && currentSort?.order === 'asc') {
      return 'sort_order_asc';
    }
    return 'sort_order_desc';
  }

  isSortedBy(sortProp: 'sort' | 'columnSort', item) {
    return this.widget.payload[sortProp]?.field === item;
  }

  sortChartItems($event, sortProp: 'sort' | 'columnSort', item) {
    $event.detail.nativeEvent.stopImmediatePropagation();

    const currentSort = this.widget.payload[sortProp] || ({} as ChartSort);

    if (
      currentSort.field !== 'custom' &&
      currentSort === this.widget.payload[sortProp] &&
      currentSort.field === item
    ) {
      currentSort.order = currentSort.order === 'asc' ? 'desc' : 'asc';
    } else {
      currentSort.field = item;
      currentSort.order = DEFAULT_SORT.order;
    }

    currentSort.items = this.sortByLabelFn(currentSort, sortProp);

    this.dataSource.update(this.dataSource.data, this.dataSource.displayedColumns);

    this.widget.payload[sortProp] = currentSort;
  }

  private sortByLabelFn(currentSort, sortProp) {
    const sgn = currentSort.order === 'asc' ? 1 : -1;

    switch (sortProp) {
      case 'sort':
        const row = this.dataSource.data.find(
          (r) => r[CrosstabChartReport.KEY] === currentSort.field
        );

        if (row) {
          this.dataSource.displayedColumns = this.dataSource.displayedColumns.sort((a, b) => {
            if (a === CrosstabChartReport.KEY) {
              return -1;
            }
            if (b === CrosstabChartReport.KEY) {
              return 1;
            }

            return sgn * (row[a] - row[b]);
          });
        }

        setTimeout(() => this.fillSeparatorCell());

        return this.dataSource.displayedColumns.filter((o) => o !== CrosstabChartReport.KEY);
      case 'columnSort':
        this.dataSource.data.sort((a, b) => {
          return sgn * (a[currentSort.field] - b[currentSort.field]);
        });

        return this.dataSource.data.map((o) => o[CrosstabChartReport.KEY]);
    }
  }

  clearSort() {
    delete this.widget.payload.sort;
    delete this.widget.payload.columnSort;

    this.dataSource.update(this.dataSource.originalData, this.dataSource.originalDisplayedColumns);
    this.cdr.markForCheck();

    setTimeout(() => this.fillSeparatorCell());
  }

  onColumnReordered($event) {
    moveItemInArray(
      this.dataSource.displayedColumns,
      $event.previousIndex + 1,
      $event.currentIndex + 1
    );
    this.dataSource.update(this.dataSource.data, this.dataSource.displayedColumns);
    this.cdr.markForCheck();

    setTimeout(() => this.fillSeparatorCell(), 100);
  }

  onRowReordered($event) {
    moveItemInArray(this.dataSource.data, $event.previousIndex, $event.currentIndex);
    this.dataSource.update(this.dataSource.data, this.dataSource.displayedColumns);
    this.isRowDraggable = false;
    this.cdr.markForCheck();

    setTimeout(() => this.fillSeparatorCell());
  }

  toggleRowDraggable(i, draggable, $event) {
    if (!this.isCustomSortMode || i !== 0 || $event.target.closest('.pinned-comment-container')) {
      return;
    }

    this.isRowDraggable = draggable;
  }

  finishCustomSort(confirm) {
    if (confirm) {
      this.widget.payload.sort = {
        field: 'custom',
        order: 'desc',
        items: this.dataSource.data.map((o) => o[CrosstabChartReport.KEY]),
      };
      this.widget.payload.columnSort = {
        field: 'custom',
        order: 'desc',
        items: this.dataSource.displayedColumns.filter((o) => o !== CrosstabChartReport.KEY),
      };
    } else {
      this.dataSource.update(this.rowsBeforeCustomOrder, this.columnsBeforeCustomOrder);
    }
    this.dashboardStore.markDirty();
    this.cdr.markForCheck();

    setTimeout(() => this.fillSeparatorCell());
    this.isCustomSortModeChange.emit(false);
  }

  longFieldName(value: string): string {
    return Utils.truncate(FieldService.getFieldLongName(value), 10);
  }

  headerSplitterTooltip(value: string): string {
    const fieldName = FieldService.getFieldLongName(value);
    return fieldName.slice(0, 1).toUpperCase() + fieldName.slice(1);
  }

  public openCrosstabSmartSummary(item, column, axis: 'column' | 'row', axisItems?) {
    const widgetQueryOptions = this.getCrossTabWidgetQueryOptions(
      item,
      column,
      axis,
      axisItems?.sort()
    );
    const queryString = this.filterService.getQueryForDocuments(widgetQueryOptions);

    super.openSmartSummary(item, queryString);

    this.cdr.markForCheck();
  }

  async downloadSmartSummarySpotERequest(item, column, axis: 'column' | 'row', axisItems) {
    this.downloadingSpoteRequest = true;
    this.downloadSmartSummaryRequestToolTip = this.translocoService.translate(
      'widgets.smart-summary.currently-downloading-spote-request'
    );
    try {
      const widgetQueryOptions = this.getCrossTabWidgetQueryOptions(item, column, axis, axisItems);
      const queryString = this.filterService.getQueryForDocuments(widgetQueryOptions);
      const analyticItemType = widgetQueryOptions.analyticItem;
      const spotERequest = await this.interactiveSmartSummaryService.getSpotEReqestForSummary(
        this.widgetDataSource.collectionName,
        this.widgetDataSource.groupId,
        this.widgetDataSource.analysisId,
        {
          paragraphLength: 1,
          analyticItemName: item,
          analyticItemType,
        },
        this.timezoneService.timezone,
        queryString,
        this.widget.payload.projectId
      );
      const jsonString = JSON.stringify(spotERequest);
      const filename = `${analyticItemType}_${item}_spote_request.json`;
      const blob = new Blob([jsonString], { type: 'application/json' });
      saveAs(blob, filename);
    } finally {
      this.downloadingSpoteRequest = false;
      this.downloadSmartSummaryRequestToolTip = '';
    }
  }

  protected openSmartSummaryModal(
    pointName: any,
    smartSummary: any,
    query?: any,
    filter?: any
  ): void {
    super.openSmartSummaryModal(pointName, smartSummary, query, filter);

    this.cdr.markForCheck();
  }

  override getSmartSummariesForPoint(pointName) {
    // for backwards compatibility
    const widgetQueryOptions = this.getCrossTabWidgetQueryOptions(
      this.itemClicked.item,
      this.itemClicked.column,
      this.itemClicked.axis,
      this.itemClicked.axisItems
    );
    const query = this.filterService.getQueryForDocuments(widgetQueryOptions);

    const sortedWidgetQueryOptions = this.getCrossTabWidgetQueryOptions(
      this.itemClicked.item,
      this.itemClicked.column,
      this.itemClicked.axis,
      this.itemClicked.axisItems?.sort()
    );
    const sortedQuery = this.filterService.getQueryForDocuments(sortedWidgetQueryOptions);

    return this.storedSmartSummaries?.filter(
      (s) =>
        !s.id.startsWith('TK') &&
        s.payload.analyticItemName === pointName &&
        s.payload.analyticItemType === (this.widget.payload.analyticItem ?? 'metadata') &&
        (s.payload.query === query || s.payload.query === sortedQuery)
    );
  }

  private getCrossTabWidgetQueryOptions(item, column, axis: 'column' | 'row', axisItems) {
    const widgetQueryOptions = this.widgetQueryOptions;
    widgetQueryOptions.pointName = item;
    widgetQueryOptions.parentPointName = column;
    widgetQueryOptions.selectedSection = this.payload.rowSelectedSections;
    widgetQueryOptions.parentSelectedSections = this.payload.columnSelectedSections;

    if (axisItems) {
      if (axis === 'column') {
        widgetQueryOptions.parentAnalyticItem = widgetQueryOptions.analyticItem;
        widgetQueryOptions.parentSubType = widgetQueryOptions.subType;
        widgetQueryOptions.analyticItem = this.payload.columnAnalyticItem;
        widgetQueryOptions.subType = this.payload.columnEntityTypeFilter;
        widgetQueryOptions.parentIsMetadata =
          this.payload.customFieldSelected || this.payload.standardFieldSelected;
      }
      widgetQueryOptions.parentPointName = axisItems.join('|+|');
    }
    if (widgetQueryOptions.analyticItem === 'taxonomy') {
      widgetQueryOptions.subType = undefined;
    }
    if (widgetQueryOptions.parentAnalyticItem === 'taxonomy') {
      widgetQueryOptions.parentSubType = undefined;
    }
    return widgetQueryOptions;
  }

  itemToCommentAnalyticItem(item) {
    const pointId = this.getPointIdFrom(item).split(COMMENT_POINT_ID_SEPARATOR);
    const rowId = pointId[0];
    const columnId = pointId[1];

    return [
      {
        name: rowId,
        displayName: rowId,
        type: this.widget.payload.analyticItem,
        subType: this.widget.payload.entityTypeFilter,
      },
      {
        name: columnId,
        displayName: columnId,
        type: this.widget.payload.columnAnalyticItem,
        subType: this.widget.payload.columnEntityTypeFilter,
      },
    ];
  }

  itemToCommentPoint(item: any): CommentPoint {
    return {
      point: {
        x: item.value,
        y: item.value,
        xAxis: 0,
        yAxis: 0,
      },
      pointId: this.getPointIdFrom(item),
    };
  }

  cellToPoint(row?: string, column?: string) {
    const point: any = { item: row, column };
    if (!row) {
      point.axis = 'column';
    }
    if (!column) {
      point.axis = 'row';
    }
    return point;
  }

  getCommentsForPoint(point: any) {
    return this.commentsByPointId().get(this.getPointIdFrom(point));
  }

  getCommentChangeForComment(comment: Comment) {
    if (!comment) {
      return;
    }

    if (this.isDependencyWheel) {
      return this.chartCommentsService.getCommentChangeForComment(
        this.elementContent.nativeElement,
        comment
      );
    }

    return this.report.getCommentDataChange(
      comment,
      this.widget,
      this.dashboard,
      this.dataSource.data
    );
  }

  private getPointIdFrom(item: any) {
    let rowId = item.row?.[CrosstabChartReport.KEY] ?? item.item;
    let columnId = item.column ?? item.item;

    if (this.isDependencyWheel) {
      rowId = item.row ?? item.item;
    }

    if (item.axis === 'row' || columnId == null) {
      columnId = '';
    }

    if (item.axis === 'column' || rowId == null) {
      rowId = '';
    }

    return `${rowId}${COMMENT_POINT_ID_SEPARATOR}${columnId}`;
  }

  getVisibleComments(): Comment[] {
    return super
      .getVisibleComments()
      .filter((c) => this.report?.isCommentVisible(c, this.widget, this.dataSource.data));
  }

  onCommentAdded(comment: Comment): void {
    super.onCommentAdded(comment);

    if (this.isDependencyWheel) {
      this.chartCommentsService.addComment(
        this.elementContent.nativeElement,
        comment,
        this.widget.payload.commentsHidden,
        this.annotationClickHandlerFn(),
        this.annotationDragHandlerFn()
      );
    }
  }

  onCommentDeleted(comment: Comment): void {
    super.onCommentDeleted(comment);

    if (this.isDependencyWheel) {
      this.chartCommentsService.removeComment(this.elementContent.nativeElement, comment);
    }
  }

  onCommentEdited(comment: Comment): void {
    super.onCommentEdited(comment);
  }

  // TODO this shall be removed once we support comments for both Dependency wheel and Crosstab views;
  // right now, it is only supported for the Crosstab, so we have to redraw the action bar
  // to hide/show the Comments button
  public handleChartOptionsChanging(): void {
    this.widgetComponent.updateTemplateBooleans();
    this.widgetComponent.initWidgetActions();
    this.widgetComponent.pillWrapper.checkCompactSize();
  }

  annotationClickHandlerFn() {
    const self = this;
    return function () {
      const point = this.labels[0].points[0];
      self.pointComments = self.report.getCommentsForPoint(point, self.payload.comments);
      self.commentsHandlerService.showExpansionPanel({
        widgetId: self.widget.id,
        widgetContainer: self.widgetComponent.widgetContainer,
        panelData: self.itemClicked,
        panelType: 'COMMENTS',
      });
    };
  }

  hideOrShowAllComments() {
    this.cdr.markForCheck();
  }

  setAnnotationSelected(commentTarget: CommentTarget) {
    super.setAnnotationSelected(commentTarget);
    this.cdr.markForCheck();
  }

  override getAiInsightDataPointCount(_handler: MatchmakerHandler): number {
    return this.dataSource.data?.length ?? 0;
  }

  override getAiInsightEligiblePointCount(): number {
    return Object.values(this.dataSource.documentCounts ?? {}).reduce(
      (count, row) =>
        count +
        Object.values(row).filter((i) => i >= Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT)
          .length,
      0
    );
  }

  override startAiInsightPointSelectionFlow(handler: MatchmakerHandler): void {
    super.startAiInsightPointSelectionFlow(handler);

    if (this.isDependencyWheel) {
      this.chartPointSelectionService.allowPointSelection(
        this.elementContent.nativeElement,
        (selection: { point: any | any[]; isSelected: boolean }[]) =>
          this.aiInsightPointSelection.next(
            selection.map((s) => ({
              point: this.createInsightPointFromDependencyWheelData(s.point),
              isSelected: s.isSelected,
            }))
          ),
        this.buildPointSelectionOptions(handler)
      );
    }
  }

  private buildPointSelectionOptions(
    handler: MatchmakerHandler
  ): ChartPointSelectionMatchmakerOptions {
    const clickablePoints = this.aiInsightValidatorService.getClickablePoints(handler);

    return {
      minPoints: clickablePoints.min,
      maxPoints: clickablePoints.max,
      pointSelectionRule: { rule: 'N/A' },
    };
  }

  override autoSelectAiInsightPoints(_handler: MatchmakerHandler) {
    const data = this.dataSource.data[0];

    this.onInsightPointSelection(data, 'right');
  }

  onInsightPointSelection(row: any, column, extraData?) {
    if (extraData) {
      return;
    }

    if (
      this.dataSource.documentCounts[row[CrosstabChartReport.KEY]][column] <
      Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT
    ) {
      this.alertService.showToast(
        translate('widgets.ai-insights.doc-threshold.below-min-snackbar', {
          count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
        })
      );
      return;
    }

    if (this.isSingleItem && row[this.key] === column) {
      this.alertService.showToast(
        translate('widgets.ai-insights.doc-threshold.no-colocation-crosstab')
      );
      return;
    }

    const pointId = this.getPointIdFrom({ row, column });
    if (this.selectedInsightPoints().includes(pointId)) {
      return;
    }

    this.insightPointSelectionState.pointCount++;
    this.selectedInsightPoints.update((points) => [...points, pointId]);

    this.aiInsightPointSelection.next([
      {
        point: this.createInsightPointFromCrosstabData(row, column),
        isSelected: true,
      },
    ]);
  }

  override endAiInsightPointSelectionFlow() {
    this.selectedInsightPoints.set([]);

    if (this.isDependencyWheel) {
      this.chartPointSelectionService.disablePointSelection(this.elementContent.nativeElement);
    }

    super.endAiInsightPointSelectionFlow();
  }

  private createInsightPointFromCrosstabData(dataPoint: any, column: string): any {
    return {
      documentCount: dataPoint[column],
      x: column,
      y: dataPoint[CrosstabChartReport.KEY],
      pointId: this.getPointIdFrom({ row: dataPoint, column }),
      series: {
        index: 0,
      } as any,
    };
  }

  private createInsightPointFromDependencyWheelData(dataPoint: any): any {
    return {
      documentCount: dataPoint.weight,
      x: dataPoint.to,
      y: dataPoint.from,
      pointId: this.getPointIdFrom({ row: dataPoint.from, column: dataPoint.to }),
      series: {
        index: 0,
      } as any,
    };
  }

  get rowColumnSeparator() {
    return COMMENT_POINT_ID_SEPARATOR;
  }

  override onCellCommentIconClicked($event: any, row: any, column: any, extraData: any): void {
    $event.stopImmediatePropagation();
    if (this.disablePinnedCommentClick) {
      return;
    }
    super.onCellCommentIconClicked($event, row, column, extraData);
  }
}
